import * as Sentry from "@sentry/react";
import { useEffect, useRef } from "react";

import posthog, { posthogEnabled } from "@/posthog";
import {
  restoreSentrySessionContext,
  syncSentryIdentity,
} from "@/services/sentry-context";

function resolvePostHogAccount({
  activeRole,
  admin,
  customer,
  sessionContext,
  therapist,
}) {
  if (
    sessionContext?.mode === "impersonation" &&
    sessionContext.actor_admin_id
  ) {
    return {
      role: "admin",
      user: { _id: sessionContext.actor_admin_id },
      sessionMode: "impersonation",
    };
  }

  const accounts = { admin, customer, therapist };
  if (!activeRole || !accounts[activeRole]?._id) return null;
  return {
    role: activeRole,
    user: accounts[activeRole],
    sessionMode: "normal",
  };
}

export function syncReplayCorrelation() {
  if (!posthogEnabled) return;

  try {
    Sentry.setTag("posthog_session_id", posthog.get_session_id() || undefined);
  } catch {
    // Observability failures must never interrupt the application.
  }

  try {
    const sentryReplayId = Sentry.getReplay()?.getSessionId?.(true);
    if (!sentryReplayId) {
      posthog.unregister_for_session("sentry_replay_id");
      return;
    }
    posthog.register_for_session({ sentry_replay_id: sentryReplayId });
  } catch {
    // Correlation is optional when either replay SDK is unavailable.
  }
}

export function useObservabilityContext({
  activeRole,
  admin,
  customer,
  pathname,
  search,
  sessionContext,
  therapist,
}) {
  const identifiedAccountRef = useRef();

  useEffect(() => {
    restoreSentrySessionContext();
  }, []);

  useEffect(() => {
    const synchronizeIdentity = async () => {
      await syncSentryIdentity({
        activeRole,
        admin,
        customer,
        therapist,
        sessionContext,
      });
      syncReplayCorrelation();
    };

    synchronizeIdentity();
  }, [activeRole, admin, customer, therapist, sessionContext]);

  useEffect(() => {
    if (!posthogEnabled) return;

    const account = resolvePostHogAccount({
      activeRole,
      admin,
      customer,
      sessionContext,
      therapist,
    });
    if (!account) {
      identifiedAccountRef.current = undefined;
      return;
    }

    const distinctId = `${account.role}:${account.user._id}`;
    if (identifiedAccountRef.current === distinctId) return;
    if (identifiedAccountRef.current) posthog.reset();

    posthog.identify(distinctId, {
      account_role: account.role,
      $internal_or_test_user: account.role === "admin",
      session_mode: account.sessionMode,
      volvamed_account_id: distinctId,
    });
    identifiedAccountRef.current = distinctId;
    syncReplayCorrelation();
  }, [activeRole, admin, customer, therapist, sessionContext]);

  useEffect(() => {
    syncReplayCorrelation();
  }, [pathname, search]);

  useEffect(() => {
    if (!posthogEnabled) return;
    return posthog.onSessionId(syncReplayCorrelation);
  }, []);
}
